"use client";

import { type ReactNode, useMemo, useState } from "react";
import { SelectField, TextField } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  NOTICE,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { chiSquareQuantile } from "@/lib/stats/distributions";
import type { NumberFormatter } from "@/lib/tool-kit/stats-distributions/format";
import {
  CHI2_ALPHAS,
  CHI2_DF_ROWS,
  type CriticalTail,
  chiSquareCritical,
  isValidAlpha,
  isValidDf,
} from "@/lib/tool-kit/stats-distributions/tables";

/** English strings. A translated content file overrides these through `ui`. */
export const CHI_SQUARE_TABLE_STRINGS = {
  dfLabel: "Degrees of freedom (df)",
  alphaLabel: "Significance level (α)",
  tailLabel: "Tail",
  tailRight: "Right tail (upper)",
  tailLeft: "Left tail (lower)",
  tailTwo: "Both tails",
  lookupHeading: "Critical value",
  resultOne: "χ² = {value}",
  resultTwo: "χ² = {lower} and {upper}",
  explainRight:
    "With {df} df, an area of {alpha} lies above {value}: reject H₀ when χ² ≥ {value}.",
  explainLeft: "With {df} df, an area of {alpha} lies below {value}.",
  explainTwo:
    "With {df} df, an area of {half} lies below {lower} and {half} above {upper} — the bounds for a two-sided test of a variance or a {confidence}% confidence interval for σ².",
  enterDf: "Enter degrees of freedom greater than 0 — decimals are fine.",
  enterAlpha: "Enter α between 0 and 1, such as 0.05.",
  caption: "Critical values of the chi-square distribution",
  areaRow: "Area to the right (α)",
  dfHeader: "df",
  print: "Print table",
  printNote:
    "Each entry is the χ² value with an area of α to its right. For the value with an area α to its left, read the column headed 1 − α.",
} as const;

type Strings = typeof CHI_SQUARE_TABLE_STRINGS;

const HIGHLIGHT = "bg-primary/15 font-semibold text-foreground";
const HEADER_HIGHLIGHT = "bg-primary/10";

/** Three decimals, or three significant digits for the tiny left-tail values. */
function tableValue(value: number, fmt: NumberFormatter): string {
  if (value < 0.1) {
    return fmt(value, {
      minimumSignificantDigits: 3,
      maximumSignificantDigits: 3,
    });
  }
  return fmt(value, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

function lookupValue(value: number, fmt: NumberFormatter): string {
  if (value < 0.1) {
    return fmt(value, {
      minimumSignificantDigits: 4,
      maximumSignificantDigits: 4,
    });
  }
  return fmt(value, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
}

/** Columns (by right-tail area) a lookup lands on, for highlighting. */
function columnsFor(alpha: number, tail: CriticalTail): number[] {
  const areas: Record<CriticalTail, number[]> = {
    right: [alpha],
    left: [1 - alpha],
    two: [1 - alpha / 2, alpha / 2],
  };
  return areas[tail]
    .map((area) => CHI2_ALPHAS.findIndex((a) => Math.abs(a - area) < 1e-12))
    .filter((index) => index !== -1);
}

function ChiSquareTableBlock({
  highlightRow,
  highlightColumns,
  strings,
  fmt,
}: {
  highlightRow: number;
  highlightColumns: number[];
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const values = useMemo(
    () =>
      CHI2_DF_ROWS.map((df) =>
        CHI2_ALPHAS.map((alpha) => chiSquareCritical(df, alpha))
      ),
    []
  );
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card print:overflow-visible">
      <table className="w-full border-collapse font-mono text-sm tabular-nums">
        <caption className="px-3 pt-3 pb-1 text-left font-medium font-sans text-foreground text-sm">
          {strings.caption}
        </caption>
        <thead>
          <tr>
            <td className="sticky left-0 bg-card" />
            <th
              className="px-2 pt-1.5 text-center font-medium font-sans text-muted-foreground text-xs"
              colSpan={CHI2_ALPHAS.length}
              scope="colgroup"
            >
              {strings.areaRow}
            </th>
          </tr>
          <tr className="border-border border-b">
            <th
              className="sticky left-0 bg-card px-2 py-1.5 text-left font-semibold"
              scope="col"
            >
              {strings.dfHeader}
            </th>
            {CHI2_ALPHAS.map((alpha, index) => (
              <th
                className={`whitespace-nowrap px-2 py-1.5 text-right font-semibold ${highlightColumns.includes(index) ? HEADER_HIGHLIGHT : ""}`}
                key={alpha}
                scope="col"
              >
                {fmt(alpha, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 3,
                })}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {CHI2_DF_ROWS.map((df, row) => (
            <tr className="border-border/60 border-b last:border-0" key={df}>
              <th
                className={`sticky left-0 whitespace-nowrap bg-card px-2 py-1 text-left font-semibold ${row === highlightRow ? HEADER_HIGHLIGHT : ""}`}
                scope="row"
              >
                {fmt(df)}
              </th>
              {CHI2_ALPHAS.map((alpha, column) => (
                <td
                  className={`whitespace-nowrap px-2 py-1 text-right text-muted-foreground ${row === highlightRow && highlightColumns.includes(column) ? HIGHLIGHT : ""}`}
                  key={alpha}
                >
                  {tableValue(values[row][column], fmt)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function lookupBody(
  df: number,
  alpha: number,
  tail: CriticalTail,
  strings: Strings,
  fmt: NumberFormatter
): ReactNode {
  const dfText = fmt(df, { maximumFractionDigits: 4 });
  const alphaText = fmt(alpha, { maximumFractionDigits: 6 });
  if (tail === "two") {
    const lower = lookupValue(chiSquareQuantile(alpha / 2, df), fmt);
    const upper = lookupValue(chiSquareQuantile(1 - alpha / 2, df), fmt);
    return (
      <>
        <p className="font-mono font-semibold text-2xl text-foreground">
          {t(strings.resultTwo, { lower, upper })}
        </p>
        <p className="text-muted-foreground">
          {t(strings.explainTwo, {
            df: dfText,
            half: fmt(alpha / 2, { maximumFractionDigits: 6 }),
            lower,
            upper,
            confidence: fmt((1 - alpha) * 100, { maximumFractionDigits: 4 }),
          })}
        </p>
      </>
    );
  }
  const critical =
    tail === "right"
      ? chiSquareCritical(df, alpha)
      : chiSquareQuantile(alpha, df);
  const value = lookupValue(critical, fmt);
  return (
    <>
      <p className="font-mono font-semibold text-2xl text-foreground">
        {t(strings.resultOne, { value })}
      </p>
      <p className="text-muted-foreground">
        {t(tail === "right" ? strings.explainRight : strings.explainLeft, {
          df: dfText,
          alpha: alphaText,
          value,
        })}
      </p>
    </>
  );
}

export default function ChiSquareTableWidget() {
  const strings = useToolUi(CHI_SQUARE_TABLE_STRINGS);
  const fmt = useToolNumber();
  const [dfText, setDfText] = useState("10");
  const [alphaInput, setAlphaInput] = useState("0.05");
  const [tail, setTail] = useState<CriticalTail>("right");

  const parsedDf = parseNumberInput(dfText);
  const parsedAlpha = parseNumberInput(alphaInput);
  const df = parsedDf !== null && isValidDf(parsedDf) ? parsedDf : null;
  const alpha =
    parsedAlpha !== null && isValidAlpha(parsedAlpha) ? parsedAlpha : null;
  let body: ReactNode = (
    <p className="text-muted-foreground">{strings.enterDf}</p>
  );
  if (df !== null && alpha === null) {
    body = <p className="text-muted-foreground">{strings.enterAlpha}</p>;
  } else if (df !== null && alpha !== null) {
    body = lookupBody(df, alpha, tail, strings, fmt);
  }

  return (
    <>
      <section className="grid gap-4 sm:grid-cols-3 print:hidden">
        <TextField
          id="ct-df"
          label={strings.dfLabel}
          numeric
          onChange={setDfText}
          value={dfText}
        />
        <TextField
          id="ct-alpha"
          label={strings.alphaLabel}
          numeric
          onChange={setAlphaInput}
          value={alphaInput}
        />
        <SelectField
          id="ct-tail"
          label={strings.tailLabel}
          onChange={setTail}
          options={[
            { value: "right", label: strings.tailRight },
            { value: "left", label: strings.tailLeft },
            { value: "two", label: strings.tailTwo },
          ]}
          value={tail}
        />
      </section>
      <div
        aria-live="polite"
        className={`${CARD} space-y-2 text-sm print:hidden`}
      >
        <h2 className={WIDGET_H2}>{strings.lookupHeading}</h2>
        {body}
      </div>
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
          <p className={`${NOTICE} flex-1`}>{strings.printNote}</p>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => window.print()}
            type="button"
          >
            {strings.print}
          </button>
        </div>
        <ChiSquareTableBlock
          fmt={fmt}
          highlightColumns={alpha === null ? [] : columnsFor(alpha, tail)}
          highlightRow={df === null ? -1 : CHI2_DF_ROWS.indexOf(df)}
          strings={strings}
        />
      </section>
    </>
  );
}
